import {
  Checkbox as MuiCheckbox,
  FormControlLabel,
  FormGroup,
  Box,
  styled,
  FormHelperText,
} from "@mui/material";
import { useEffect } from "react";
import {
  Controller,
  FieldPath,
  FieldValues,
  get,
  Path,
  PathValue,
  useFormContext,
  useWatch,
} from "react-hook-form";
import { getErrorMessage } from "../fieldTypes";

export interface CheckboxOption {
  label: string;
  value: string;
}

export interface CheckboxGroupProps<
  FormValues extends FieldValues = FieldValues,
> {
  name: FieldPath<FormValues>;
  label: string;
  options: CheckboxOption[];
  indent?: number;
}

interface CheckboxGroupValue {
  main?: boolean;
  items?: Record<string, boolean>;
}

const StyledMuiCheckbox = styled(MuiCheckbox)`
  &.MuiCheckbox-root {
    padding: 2px 9px;
  }
`;

const CheckboxGroup = <FormValues extends FieldValues = FieldValues>({
  name,
  label,
  options,
  indent = 2,
}: CheckboxGroupProps<FormValues>) => {
  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext<FormValues>();

  const mainChecked = useWatch({ control, name }) as CheckboxGroupValue;
  const mainValue = mainChecked?.main ?? false;

  const mainError = getErrorMessage(get(errors, `${name}.main`));
  const itemsErrors = get(errors, `${name}.items`) as unknown;
  const itemErrorEntries =
    typeof itemsErrors === "object" && itemsErrors !== null
      ? Object.entries(itemsErrors)
      : [];
  const setBooleanValue = (path: Path<FormValues>, value: boolean) => {
    setValue(path, value as PathValue<FormValues, Path<FormValues>>);
  };

  useEffect(() => {
    if (!mainValue) {
      options.forEach((opt) => {
        setBooleanValue(
          `${name}.items.${opt.value}` as Path<FormValues>,
          false
        );
      });
    }
  }, [mainValue, name, options, setValue]);

  return (
    <FormGroup>
      <Controller
        name={`${name}.main` as Path<FormValues>}
        control={control}
        defaultValue={false as PathValue<FormValues, Path<FormValues>>}
        render={({ field }) => (
          <FormControlLabel
            control={
              <MuiCheckbox
                {...field}
                checked={!!field.value}
                color={!get(errors, name) ? "primary" : "error"}
              />
            }
            label={label}
          />
        )}
      />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          pl: indent,
          opacity: mainChecked ? 1 : 0.6,
        }}
      >
        {options.map((opt) => (
          <Controller
            key={opt.value}
            name={`${name}.items.${opt.value}` as Path<FormValues>}
            control={control}
            defaultValue={false as PathValue<FormValues, Path<FormValues>>}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <StyledMuiCheckbox
                    {...field}
                    checked={!!field.value}
                    disabled={!mainValue}
                  />
                }
                label={opt.label}
              />
            )}
          />
        ))}
      </Box>
      {mainError && <FormHelperText error>{mainError}</FormHelperText>}
      {itemErrorEntries.map(([key, error]) => {
        const message = getErrorMessage(error);

        return (
          message && (
            <FormHelperText key={key} error>
              {message}
            </FormHelperText>
          )
        );
      })}
    </FormGroup>
  );
};

export { CheckboxGroup };
